import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Button,
  Link as MuiLink,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

const sections = [
  { id: "Overview", label: "Overview" },
  { id: "Topics", label: "Topics" },
  { id: "Submission-Guidelines", label: "Submission Guidelines" },
  { id: "How-to-Submit", label: "How to Submit" },
  { id: "Dates-and-Deadlines", label: "Dates and Deadlines" },
  { id: "About-the-Hub", label: "About the Hub" },
  { id: "Contact", label: "Contact" },
];

const LAST_UPDATED_DATE = "2026-09-16";

const PROPOSAL_SUBMISSION_URL =
  "https://usmforms.formstack.com/forms/2027_mdhh_paper_submission";

const Conference = () => {
  const [activeSection, setActiveSection] = useState("Overview");

  const handleScroll = () => {
    const container = document.getElementById("conference-scrollable-content");

    if (!container) return;

    const scrollPosition = container.scrollTop;

    const offsets = sections.map(({ id }) => {
      const el = document.getElementById(id);

      return {
        id,
        offset: el ? el.offsetTop - container.offsetTop : Infinity,
      };
    });

    const current = offsets
      .filter(({ offset }) => offset <= scrollPosition + 10)
      .pop();

    if (current && current.id !== activeSection) {
      setActiveSection(current.id);
    }
  };

  useEffect(() => {
    const container = document.getElementById("conference-scrollable-content");

    if (!container) return;

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [activeSection]);

  const handleScrollToSection = (sectionId) => {
    const container = document.getElementById("conference-scrollable-content");

    const target = document.getElementById(sectionId);

    if (!container || !target) return;

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      const yOffset = -150;

      const y =
        target.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({
        top: y,
        behavior: "smooth",
      });
    } else {
      const scrollTop = target.offsetTop - container.offsetTop;

      container.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    }

    setActiveSection(sectionId);
  };

  const bodyTextStyle = {
    marginBottom: "24px",
    lineHeight: 1.8,
    textAlign: "left",
  };

  const sectionTitleStyle = {
    fontWeight: 500,
    mb: 2,
  };

  const infoLabelStyle = {
    fontWeight: 600,
    color: "#222222",
  };

  return (
    <Box
      sx={{
        width: "auto",
        margin: "0px auto",
        padding: {
          xs: "20px",
          sm: "30px",
          md: "1px 80px",
        },
        border: "none",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          pt: { xs: 2, md: 4 },
          pb: 4,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            letterSpacing: "1px",
          }}
        >
          Call for Papers
        </Typography>

        <Typography
          variant="h3"
          sx={{
            fontWeight: 500,
            mt: 0.5,
            mb: 2,
            fontSize: {
              xs: "2rem",
              sm: "2.15rem",
              md: "2.5rem",
            },
          }}
        >
          Digital Mississippi: The Past, Present, and Future of the Digital
          Humanities{" "}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            fontSize: "1.05rem",
            lineHeight: 1.8,
            maxWidth: "850px",
            mx: "auto",
            mb: 3,
          }}
        >
          Hosted by the <em>Mississippi Digital Humanities Hub</em> at The
          University of Southern Mississippi and the{" "}
          <em>Society of Mississippi Archivists</em>.
        </Typography>

        <Box
          sx={{
            maxWidth: "850px",
            mx: "auto",
            mb: 3,
            p: {
              xs: 2,
              sm: 2.5,
            },
            backgroundColor: "#fff7db",
            borderLeft: "4px solid #FFD700",
            textAlign: "left",
          }}
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            <Box component="span" sx={infoLabelStyle}>
              Conference Dates:
            </Box>{" "}
            April 8–9, 2027
          </Typography>

          <Typography variant="body1" sx={{ mb: 1 }}>
            <Box component="span" sx={infoLabelStyle}>
              Location:
            </Box>{" "}
            Trent Lott National Center, The University of Southern Mississippi,
            Hattiesburg, MS
          </Typography>

          <Typography variant="body1">
            <Box component="span" sx={infoLabelStyle}>
              Submission Deadline:
            </Box>{" "}
            January 22, 2027
          </Typography>
        </Box>

        <Button
          variant="contained"
          href={PROPOSAL_SUBMISSION_URL || undefined}
          target={PROPOSAL_SUBMISSION_URL ? "_blank" : undefined}
          rel={PROPOSAL_SUBMISSION_URL ? "noopener noreferrer" : undefined}
          disabled={!PROPOSAL_SUBMISSION_URL}
          sx={{
            backgroundColor: "#FFD700",
            color: "#111111",
            padding: "12px 26px",
            fontWeight: 700,
            minWidth: "190px",
            textTransform: "none",
            borderRadius: 1,

            "&:hover": {
              backgroundColor: "#FFB800",
            },

            "&.Mui-disabled": {
              backgroundColor: "#f3df82",
              color: "#666666",
            },
          }}
        >
          Submit a Proposal
        </Button>

        {!PROPOSAL_SUBMISSION_URL && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 1.5,
            }}
          >
            Submission portal link coming soon.
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: {
            xs: "block",
            md: "flex",
          },

          height: {
            xs: "auto",
            md: "100vh",
          },
        }}
      >
        <Box
          sx={{
            width: {
              xs: "100%",
              md: 250,
            },

            borderRight: {
              xs: "none",
              md: "1px solid #ccc",
            },

            borderBottom: {
              xs: "1px solid #ccc",
              md: "none",
            },

            p: 2,

            height: {
              xs: "auto",
              md: "100vh",
            },

            overflowX: {
              xs: "auto",
              md: "visible",
            },

            overflowY: {
              xs: "hidden",
              md: "auto",
            },

            whiteSpace: {
              xs: "nowrap",
              md: "normal",
            },

            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",

            backgroundColor: "#f9f9f9",
            zIndex: 10,
            position: "relative",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: {
                xs: "block",
                md: "none",
              },

              color: "gray",
              mb: 1,
              pl: 2,
            }}
          >
            Swipe sideways to explore all sections →
          </Typography>

          <Box
            sx={{
              position: "relative",
              width: "100%",

              overflowX: {
                xs: "auto",
                md: "visible",
              },

              flex: 1,

              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: 0,
                height: "100%",
                width: 30,

                background:
                  "linear-gradient(to left, #f9f9f9 60%, transparent)",

                pointerEvents: "none",

                display: {
                  xs: "block",
                  md: "none",
                },
              },
            }}
          >
            <List
              sx={{
                display: "flex",

                flexDirection: {
                  xs: "row",
                  md: "column",
                },

                width: "100%",
                flex: 1,

                overflowY: {
                  xs: "hidden",
                  md: "auto",
                },

                scrollSnapType: {
                  xs: "x mandatory",
                  md: "none",
                },

                scrollPaddingX: "16px",
              }}
            >
              {sections.map((section) => (
                <ListItemButton
                  key={section.id}
                  onClick={() => handleScrollToSection(section.id)}
                  sx={{
                    minWidth: {
                      xs: 175,
                      md: "auto",
                    },

                    px: 2,
                    flexShrink: 0,
                    whiteSpace: "normal",

                    scrollSnapAlign: {
                      xs: "start",
                      md: "none",
                    },

                    backgroundColor:
                      activeSection === section.id ? "#ffd700" : "transparent",

                    "&:hover": {
                      backgroundColor:
                        activeSection === section.id ? "#ffd700" : "#f0f0f0",
                    },

                    transition: "background-color 0.2s ease",
                  }}
                >
                  <ListItemText primary={section.label} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Box>

        <Box
          id="conference-scrollable-content"
          sx={{
            flex: 1,

            p: {
              xs: 2,
              sm: 3,
              md: 4,
            },

            overflowY: "auto",
            scrollBehavior: "smooth",

            height: {
              xs: "auto",
              md: "100vh",
            },
          }}
        >
          <Typography
            variant="h5"
            id="Overview"
            gutterBottom
            sx={sectionTitleStyle}
          >
            Conference Overview
          </Typography>

          <Typography variant="body1" sx={bodyTextStyle}>
            Mississippi has a rich history of storytelling and the next frontier
            is digital. The Mississippi Digital Humanities Hub, in collaboration
            with the Society of Misssissippi Archivists, invites proposals for
            presentations that celebrate digitization and the Digital Humanities
            in Mississippi. We are looking to highlight digital projects,
            completed or currently in progress, as well as the use of digital
            techniques, tools, and pedagogy at museums, libraries, genealogical
            societies and institutions of learning throughout the state.
            Archivists, librarians, public historians, curators, educators,
            independent researchers, graduate students, and undergraduate
            students, based in Mississippi or working on projects related to
            Mississippi’s history and culture, are encouraged to submit
            proposals. For more information on the archival side of the
            conference, view the sister CFP here (coming soon).
          </Typography>

          <Typography variant="body1" sx={bodyTextStyle}>
            The conference is free and open to the public. Registered attendees
            will be provided with a light dinner on Thursday evening.
          </Typography>

          {/* =================================================
              TOPICS
          ================================================= */}

          <Typography
            variant="h5"
            id="Topics"
            gutterBottom
            sx={sectionTitleStyle}
          >
            Topics
          </Typography>

          <Typography variant="body1" sx={{ ...bodyTextStyle, mb: 2 }}>
            We invite proposals on a broad range of topics and tools, including
            but not limited to:
          </Typography>

          <Box
            component="ul"
            sx={{
              pl: 3,
              mt: 0,
              mb: 4,

              "& li": {
                mb: 1.5,
                lineHeight: 1.8,
              },
            }}
          >
            <li>
              <strong>Computational Textual Analysis:</strong> Natural language
              processing, text mining, and stylistic analysis
            </li>

            <li>
              <strong>Spatial and Temporal Humanities:</strong> GIS, mapping
              projects, and digital archaeology
            </li>

            <li>
              <strong>Digital Archive and Cultural Heritage:</strong> Community
              archives, metadata standards, and digital preservation
            </li>

            <li>
              <strong>Artificial Intelligence and the Humanities:</strong>{" "}
              Generative AI, machine learning ethics, and algorithmic bias
            </li>

            <li>
              <strong>Public Humanities and Pedagogy:</strong> Digital
              storytelling, open-access publishing, and teaching with digital
              tools
            </li>

            <li>
              <strong>Critical Digital Humanities:</strong> Postcolonial,
              feminist, and other approaches, and accessible approaches to data
              presentation and management
            </li>
          </Box>

          <Typography
            variant="h5"
            id="Submission-Guidelines"
            gutterBottom
            sx={sectionTitleStyle}
          >
            Submission Guidelines
          </Typography>

          <Typography variant="body1" sx={{ ...bodyTextStyle, mb: 2 }}>
            We are accepting proposals for two presentation formats:
          </Typography>

          <Box
            sx={{
              border: "1px solid #e2e2e2",
              borderRadius: 1,
              p: 2.5,
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "1.05rem",
                fontWeight: 600,
                mb: 1,
              }}
            >
              Papers — 15 minutes
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              Papers (15 minutes) should include a title, a 250-350 word
              abstract, and a brief biography (100 words).
            </Typography>
          </Box>

          <Box
            sx={{
              border: "1px solid #e2e2e2",
              borderRadius: 1,
              p: 2.5,
              mb: 4,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "1.05rem",
                fontWeight: 600,
                mb: 1,
              }}
            >
              Papers — 50 minutes
            </Typography>

            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                mb: 1.5,
              }}
            >
              Papers (50 minutes) should include a title and a 300-500 word
              abstract, and a brief biography (100 words) for each participants,
              and a 300-500 word abstract.
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              Fifty minute slots are reserved for team presentations or for
              presentations that teach digital humanities skills and
              digitization techniques.
            </Typography>
          </Box>

          <Typography
            variant="h5"
            id="How-to-Submit"
            gutterBottom
            sx={sectionTitleStyle}
          >
            How to Submit
          </Typography>

          <Typography variant="body1" sx={bodyTextStyle}>
            All proposals should be submitted via our online portal. Abstracts
            and bios should be prepared in advance and uploaded when applying.
          </Typography>

          <Button
            variant="contained"
            href={PROPOSAL_SUBMISSION_URL || undefined}
            target={PROPOSAL_SUBMISSION_URL ? "_blank" : undefined}
            rel={PROPOSAL_SUBMISSION_URL ? "noopener noreferrer" : undefined}
            disabled={!PROPOSAL_SUBMISSION_URL}
            sx={{
              backgroundColor: "#FFD700",
              color: "#111111",
              padding: "10px 22px",
              fontWeight: 700,
              textTransform: "none",
              mb: 1,

              "&:hover": {
                backgroundColor: "#FFB800",
              },

              "&.Mui-disabled": {
                backgroundColor: "#f3df82",
                color: "#666666",
              },
            }}
          >
            Submit a Proposal
          </Button>

          {!PROPOSAL_SUBMISSION_URL && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Submission portal link coming soon.
            </Typography>
          )}

          <Typography
            variant="h5"
            id="Dates-and-Deadlines"
            gutterBottom
            sx={sectionTitleStyle}
          >
            Dates and Deadlines
          </Typography>

          <Box
            sx={{
              backgroundColor: "#fff7db",
              borderLeft: "4px solid #FFD700",
              p: 2.5,
              mb: 4,
            }}
          >
            <Typography variant="body1" sx={{ mb: 1.5 }}>
              <Box component="span" sx={infoLabelStyle}>
                Submission Deadline:
              </Box>{" "}
              January 22, 2027
            </Typography>

            <Typography variant="body1" sx={{ mb: 1.5 }}>
              <Box component="span" sx={infoLabelStyle}>
                Notification of Acceptance:
              </Box>{" "}
              February 5, 2027
            </Typography>

            <Typography variant="body1">
              <Box component="span" sx={infoLabelStyle}>
                Conference Registration Opens:
              </Box>{" "}
              February 12, 2027
            </Typography>
          </Box>

          <Typography
            variant="h5"
            id="About-the-Hub"
            gutterBottom
            sx={sectionTitleStyle}
          >
            About the Mississippi Digital Humanities Hub
          </Typography>

          <Typography variant="body1" sx={bodyTextStyle}>
            The Mississippi Digital Humanities Hub is a grant-funded initiative
            of the Center for Digital Humanities at The University of Southern
            Mississippi. The Hub receives Congressionally allocated funding
            administered by the National Historical Publications and Records
            Commission (NHPRC), a part of the National Archives and Records
            Administration (NARA). For more information on the Hub, please visit{" "}
            <MuiLink
              href="/about"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "inherit",
                fontWeight: 500,
                textDecoration: "underline",
                "&:hover": {
                  color: "inherit",
                  textDecoration: "underline",
                },
              }}
            >
              About Us
            </MuiLink>{" "}
            page.
          </Typography>

          <Typography
            variant="h5"
            id="Contact"
            gutterBottom
            sx={sectionTitleStyle}
          >
            Questions?
          </Typography>

          <Typography variant="body1" sx={bodyTextStyle}>
            <Box component="span" sx={infoLabelStyle}>
              Contact email:
            </Box>{" "}
            <MuiLink
              href="mailto:Samantha.L.Stephens@usm.edu"
              sx={{
                color: "inherit",
                fontWeight: 500,
                textDecoration: "underline",
                "&:hover": {
                  color: "inherit",
                  textDecoration: "underline",
                },
              }}
            >
              Samantha.L.Stephens@usm.edu
            </MuiLink>
          </Typography>
        </Box>
      </Box>

      {/*  <Typography
        variant="body2"
        sx={(theme) => ({
          textAlign: "center",
          textTransform: "uppercase",
          fontWeight: 500,
          fontSize: "0.875rem",
          mb: 2,
          mt: 2,
          letterSpacing: "0.2px",

          color: alpha(theme.palette.text.secondary, 0.45),
        })}
      >
        Revised:{" "}
        {new Date(LAST_UPDATED_DATE).toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })}
      </Typography>
      */}
    </Box>
  );
};

export default Conference;
